import { normalizeResourceOptionLabels } from "../../helpers/resourceLabels";
import { createSlice, createAsyncThunk, createEntityAdapter } from "@reduxjs/toolkit";
import appointmentApiController from "../../services/appointment.service";
import HandleError from "../HandleError";
import HandleResponse from "../HandleResponse";
import { normalizeAppointmentDetail } from "../../helpers/appointmentDetail";
import { getFrameWindow, mergeCalendarFrame, withCalendarListIdentity } from "../../pages/calendar/calendarMutationRefresh";
import { contentErrorMessage } from "./contentError";
export const usersAdapter = createEntityAdapter();

// Redux Toolkit stamps every rejectWithValue action with this transport
// placeholder, which is not a user-facing message. The shared resolver also
// unwraps the authenticated API error envelope ({"error": {"message": ...}}),
// which the previous flat lookup missed, so the calendar fell back to
// "Rejected" whenever a read failed.
const transportPlaceholder = "Rejected";

const listErrorMessage = (action, fallback) => {
  const message = contentErrorMessage(action, fallback);
  const readable = typeof message === "string" ? message.trim() : "";
  if (!readable || readable === transportPlaceholder) return fallback;
  return readable;
};

const isCanceledRequest = (error, thunkAPI) => (
  thunkAPI.signal.aborted ||
  error?.code === "ERR_CANCELED" ||
  error?.name === "CanceledError" ||
  error?.name === "AbortError"
);

export const appointmentStoreApi = createAsyncThunk("appointment/create", async (formvalues, thunkAPI) => {
  try {
    const resposedata = await appointmentApiController
      .create(formvalues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "create"))
      .catch((error) => HandleError(thunkAPI, error, "create"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue({ status: error.response.status, message: message });
  }
});

export const appointmentDepositStoreApi = createAsyncThunk("appointment/deposit/store", async (formvalues, thunkAPI) => {
  try {
    const resposedata = await appointmentApiController
      .appointmentDepositStore(formvalues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "appointmentdepositstore"))
      .catch((error) => HandleError(thunkAPI, error, "appointmentdepositstore"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue({ status: error.response && error.response.status, message });
  }
});

export const appointmentPaymentConfirmApi = createAsyncThunk("appointmentpayment/confirm", async (formvalues, thunkAPI) => {
  try {
    const resposedata = await appointmentApiController
      .appointmentpayconfirm(formvalues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "appointmentpaymentconfirm"))
      .catch((error) => HandleError(thunkAPI, error, "appointmentpaymentconfirm"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue({ status: error.response.status, message: message });
  }
});
export const refundPaymentStoreApi = createAsyncThunk("refundpayment/store", async (formvalues, thunkAPI) => {
  try {
    const resposedata = await appointmentApiController
      .refundpaymentstore(formvalues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "refundpaymentstore"))
      .catch((error) => HandleError(thunkAPI, error, "refundpaymentstore"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue({ status: error.response.status, message: message });
  }
});

export const appointmentPaymentFailedApi = createAsyncThunk("appointmentpayment/paymentfailed", async (formvalues, thunkAPI) => {
  try {
    const resposedata = await appointmentApiController
      .appointmentpayfail(formvalues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "appointmentpaymentfailed"))
      .catch((error) => HandleError(thunkAPI, error, "appointmentpaymentfailed"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue({ status: error.response.status, message: message });
  }
});

export const appointmentUpdateApi = createAsyncThunk("appointment/update", async (formvalues, thunkAPI) => {
  try {
    const resposedata = await appointmentApiController
      .update(formvalues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "update"))
      .catch((error) => HandleError(thunkAPI, error, "update"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue({ status: error.response.status, message: message });
  }
});

export const appointmentRescheduleApi = createAsyncThunk("appointment/reschedule", async (formvalues, thunkAPI) => {
  try {
    const resposedata = await appointmentApiController
      .reschedule(formvalues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "reschedule"))
      .catch((error) => HandleError(thunkAPI, error, "reschedule"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue({ status: error.response.status, message: message });
  }
});

export const appointmentResizeApi = createAsyncThunk("appointment/resize", async (formvalues, thunkAPI) => {
  try {
    return await appointmentApiController
      .appointmentresize(formvalues)
      .then((response) => HandleResponse(thunkAPI, response, "resize"))
      .catch((error) => HandleError(thunkAPI, error, "resize"));
  } catch (error) {
    const message = error?.response?.data?.message || error.message || error.toString();
    return thunkAPI.rejectWithValue({ status: error?.response?.status, message });
  }
});

export const appointmentListViewApi = createAsyncThunk("appointment/listview", async (formValues, thunkAPI) => {
  try {
    const resposedata = await appointmentApiController
      .view(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "listview"))
      .catch((error) => HandleError(thunkAPI, error, "listview"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

/**
 * Refresh only the appointment frame that a save actually changed.
 *
 * The caller passes the range currently on screen plus the appointment(s) that
 * were saved; the request is narrowed to their day and the response is merged
 * into the existing calendar data instead of replacing it.
 */
export const appointmentFrameRefreshApi = createAsyncThunk("appointment/framerefresh", async (formValues, thunkAPI) => {
  try {
    const resposedata = await appointmentApiController
      .view(withCalendarListIdentity(formValues?.range), thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "listview"))
      .catch((error) => HandleError(thunkAPI, error, "listview"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

// Older calendar create/reschedule callers use `client`; history transport and
// cache ownership must resolve the same client before either request is used.
const clientAppointmentRequest = (values = {}) => ({
  ...values,
  client_id: values?.client_id ?? values?.client ?? "",
  page_name: values?.page_name || "client_appointment",
});

export const ClientAppointmentListViewApi = createAsyncThunk("appointment/clientappointmentlistview", async (formValues, thunkAPI) => {
  const request = clientAppointmentRequest(formValues);
  if (!request.client_id) {
    return thunkAPI.rejectWithValue({ status: 422, message: "Client is required." });
  }
  try {
    const resposedata = await appointmentApiController
      .clientappointmentview(request, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "clientappointmentlistview"))
      .catch((error) => HandleError(thunkAPI, error, "clientappointmentlistview"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const appointmentOptions = createAsyncThunk("appointment/appointmentOptions", async (formValues, thunkAPI) => {
  try {
    const resposedata = await appointmentApiController
      .view(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "appointmentOptions"))
      .catch((error) => HandleError(thunkAPI, error, "appointmentOptions"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const appointmentDetailApi = createAsyncThunk("appointment/detail", async (formValues, thunkAPI) => {
  try {
    const resposedata = await appointmentApiController
      .view(formValues, thunkAPI)
      .then((response) => {
        if (response && response.status >= 200 && response.status < 300) {
          return thunkAPI.fulfillWithValue(normalizeAppointmentDetail(response.data));
        }
        return HandleResponse(thunkAPI, response, "detail");
      })
      .catch((error) => HandleError(thunkAPI, error, "detail"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const appointmentDeleteApi = createAsyncThunk("appointment/delete", async (formValues, thunkAPI) => {
  try {
    const resposedata = await appointmentApiController
      .deleted(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "delete"))
      .catch((error) => HandleError(thunkAPI, error, "delete"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const appointmentSuggetionListApi = createAsyncThunk("appointment/suggetionlist", async (formValues, thunkAPI) => {
  try {
    const resposedata = await appointmentApiController
      .suggetionlist(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "suggetionlist"))
      .catch((error) => HandleError(thunkAPI, error, "suggetionlist"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const AppointmentAutofillserviceOptionsApi = createAsyncThunk("service/autoserviceOptions", async (formValues, thunkAPI) => {
  try {
    const resposedata = await appointmentApiController
      .appointmentautofillserviceoptions(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "autoserviceOptions"))
      .catch((error) => HandleError(thunkAPI, error, "autoserviceOptions"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const AppointmentResourceItemsApi = createAsyncThunk("appointment/resourceitem", async (formValues, thunkAPI) => {
  try {
    const response = await appointmentApiController.appointmentresourceitems(formValues, thunkAPI);
    return HandleResponse(thunkAPI, response, "appointmentresourceitem");
  } catch (error) {
    if (isCanceledRequest(error, thunkAPI)) {
      return thunkAPI.rejectWithValue({ cancelled: true });
    }
    return HandleError(thunkAPI, error, "appointmentresourceitem");
  }
});

export const ClientInvoiceSaleCartApi = createAsyncThunk("appointment/salecartsdata", async (formValues, thunkAPI) => {
  try {
    const resposedata = await appointmentApiController
      .salecarts(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "salecartsdata"))
      .catch((error) => HandleError(thunkAPI, error, "salecartsdata"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});
export const AppointmentAutoFillResourceItemsApi = createAsyncThunk("appointment/autofillresourceitem", async (formValues, thunkAPI) => {
  try {
    const resposedata = await appointmentApiController
      .appointmentresourceitems(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "appointmentautofillresourceitem"))
      .catch((error) => HandleError(thunkAPI, error, "appointmentautofillresourceitem"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const AppointmentAutoFillStaffOptionsApi = createAsyncThunk("appointment/autofillstaffoption", async (formValues, thunkAPI) => {
  try {
    const resposedata = await appointmentApiController
      .appointmentstaffoptions(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "appointmentautofillstaffoption"))
      .catch((error) => HandleError(thunkAPI, error, "appointmentautofillstaffoption"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const BookNextAvailableResourceApi = createAsyncThunk("appointment/nextavailableresource", async (formValues, thunkAPI) => {
  try {
    const resposedata = await appointmentApiController
      .resourcenextavailable(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "nextavailableresource"))
      .catch((error) => HandleError(thunkAPI, error, "nextavailableresource"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

const initialState = {
  nextResourceRequestId: null,
  isOpenedAddForm: "",
  dateBusytimeListView: [], // Ensure this is initialized as an empty array

  isOpenedEditForm: "",
  isOpenedRescheduleForm: "",
  isOpenedDetailModal: "",
  isListView: [],
  isListViewError: "",
  isClientAppointmentListView: [],
  isClientDetailAppointmentListView: [],
  clientAppointmentRequestId: null,
  clientAppointmentQuery: null,
  clientAppointmentClientId: null,
  isClientDetailAppointmentListViewError: "",
  isAppointmentPaymentFailedApi: [],
  isAppointmentPaymentConfirmApi: [],
  isDetailData: null,
  appointmentDetailRequestId: null,
  latestListViewRequestId: null,
  isFilter: "",
  isClientAppointmentFilterStatus: "",
  isAddOnServiceTab: "",
  isDataLoading: false,
  isOpenAppointmentAutoFillModal: "",
  isAppointmentAutofillData: "",
  isAppointmentAutoFillFinalData: "",
  isAppointmentAvailableTimeLoader: false,
  isAppointmentAvailableTime: "",
  isAppointmentAutofillserviceOptions: [],
  isOpenInvoiceStripePayModal: "",
  isOpenDepositPayTab: true,
  iscalendarAddFormSetActivebtnState: "",
  isServiceDepositData: "",
  isStripeTokenAppointment: "",
  isOpenAppointmentRefundModal: "",
  isGetCancelledAppointmentData: "",
  isOpenAppointmentcancelRefundStripe: "",
  isGetAppointmentcancelRefundStripe: "",
  isEditAppointmentFormData: "",
  isOpenEditAppointmentRefunds: "",
  isCancellationReason: "",
  isRangeInfoEdits: "",
  isCalendarEditFormSetActivebtnState: "",
  isdepositBalance: "",
  isCancelDepositBalance: "",
  isAddOnServiceButtonRender: false,
  isBookNextModalOpen: false,
  isOpenInvoiceStripePayModal: "",
  isOpenDepositPayTab: true,
  iscalendarAddFormSetActivebtnState: "",
  isServiceDepositData: "",
  isStaffBusinessHourArrays: [],
  isAppointmentResourceItems: [],
  appointmentResourceAvailabilityContext: null,
  latestAppointmentResourceRequestId: "",
  isClientInvoiceSaleCart: [],
  isBookNextAvailableResource: [],
  isApiStatus: { appointmentStore: "", appointmentUpdate: "", appointmentReschedule: "", appointmentListViewApi: "", ClientAppointmentList: "", appointmentDetail: "", appointmentDelete: "" },
  isApiStatus: {
    AppointmentAutofillserviceOptionsApi: "",
    appointmentStoreApi: "",
    appointmentUpdateApi: "",
    appointmentRescheduleApi: "",
    appointmentListViewApi: "",
    appointmentFrameRefreshApi: "",
    ClientAppointmentListViewApi: "",
    appointmentDetailApi: "",
    appointmentDeleteApi: "",
    appointmentPaymentConfirmApi: "",
    appointmentPaymentFailedApi: "",
    refundPaymentStoreApi: "",
    appointmentPaymentConfirmApi: "",
    appointmentPaymentStoreApi: "",
    appointmentPaymentFailedApi: "",
    AppointmentResourceItemsApi: "",
    ClientInvoiceSaleCartApi: "",
    BookNextAvailableResourceApi: ""
  },
};

const appointmentSlice = createSlice({
  name: "appointment",
  initialState,
  reducers: {
    reset: () => initialState,
    AddAppointmentForm: (state, action) => {
      state.isOpenedEditForm = "";
      state.isOpenedRescheduleForm = "";
      state.isOpenedAddForm = action.payload;
    },
    EditAppointmentForm: (state, action) => {
      state.isOpenedAddForm = "";
      state.isOpenedEditForm = action.payload;
      state.isOpenedRescheduleForm = "";
    },
    RescheduleAppointmentForm: (state, action) => {
      state.isOpenedAddForm = "";
      state.isOpenedEditForm = "";
      state.isOpenedRescheduleForm = action.payload;
    },
    AppointmentDetailModal: (state, action) => {
      state.isOpenedDetailModal = action.payload;
      if (!action.payload) state.appointmentDetailRequestId = "closed";
    },
    openAppointmentFilter: (state, action) => {
      state.isFilter = action.payload;
    },
    clientAppointmentFilterStatus: (state, action) => {
      state.isClientAppointmentFilterStatus = action.payload;
    },
    clientAppointmentFilterStatusClear: (state) => {
      state.isClientAppointmentFilterStatus = "";
    },
    ShowLoadingSpinner: (state, action) => {
      state.isDataLoading = action.payload;
    },
    closeAppointmentFilter: (state) => {
      state.isFilter = "";
    },
    AddOnServiceTab: (state, action) => {
      // if (newTodo.trim() === '') return;
      state.isAddOnServiceTab = [...state.isAddOnServiceTab, action.payload];
    },
    OpenAppointmentAutoFillModal: (state, action) => {
      state.isOpenAppointmentAutoFillModal = action.payload;
    },
    AppointmentAutofillData: (state, action) => {
      state.isAppointmentAutofillData = action.payload;
    },
    AppointmentAutoFillFinalData: (state, action) => {
      state.isAppointmentAutoFillFinalData = action.payload;
    },
    AppointmentAvailableTimeLoader: (state, action) => {
      state.isAppointmentAvailableTimeLoader = action.payload;
    },
    AppointmentAvailableTime: (state, action) => {
      state.isAppointmentAvailableTime = action.payload;
    },
    OpenInvoiceStripePayModal: (state, action) => {
      state.isOpenInvoiceStripePayModal = action.payload;
    },
    OpenDepositPayTab: (state, action) => {
      state.isOpenDepositPayTab = action.payload;
    },
    CalendarAddFormSetActivebtnState: (state, action) => {
      state.iscalendarAddFormSetActivebtnState = action.payload;
    },
    serviceDepositData: (state, action) => {
      state.isServiceDepositData = action.payload;
    },
    GetStripeTokenAppointment: (state, action) => {
      state.isStripeTokenAppointment = action.payload;
    },
    OpenAppointmentRefundModal: (state, action) => {
      state.isOpenAppointmentRefundModal = action.payload;
    },
    GetCancelledAppointmentData: (state, action) => {
      state.isGetCancelledAppointmentData = action.payload;
    },
    OpenAppointmentcancelRefundStripe: (state, action) => {
      state.isOpenAppointmentcancelRefundStripe = action.payload;
    },
    GetAppointmentcancelRefundStripe: (state, action) => {
      state.isGetAppointmentcancelRefundStripe = action.payload;
    },
    EditAppointmentFormData: (state, action) => {
      state.isEditAppointmentFormData = action.payload;
    },
    OpenEditAppointmentRefunds: (state, action) => {
      state.isOpenEditAppointmentRefunds = action.payload;
    },
    CalendarEditFormSetActivebtnState: (state, action) => {
      state.isCalendarEditFormSetActivebtnState = action.payload;
    },
    CancellationReason: (state, action) => {
      state.isCancellationReason = action.payload.selectedCancellationReason;
      state.isRangeInfoEdits = action.payload.isRangeInfo;
      state.isdepositBalance = action.payload.depositBalance;
    },
    CancelDepositBalance: (state, action) => {
      state.isCancelDepositBalance = action.payload;
    },
    AddOnServiceButtonRender: (state, action) => {
      state.isAddOnServiceButtonRender = action.payload;
    },
    ClearAppointmentDetailData: (state) => {
      state.isDetailData = null;
      state.appointmentDetailRequestId = "cleared";
    },
    StaffBusinessHourArrays: (state, action) => {
      state.isStaffBusinessHourArrays = action.payload;
    },
    AppointmentResourceItemsClears: (state) => {
      state.isAppointmentResourceItems = [];
      state.appointmentResourceAvailabilityContext = null;
      state.latestAppointmentResourceRequestId = "cleared";
    },
    BookNextModalOpen: (state, action) => {
      state.isBookNextModalOpen = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(appointmentStoreApi.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentStore: "loading" };
      })
      .addCase(appointmentStoreApi.fulfilled, (state) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentStore: "succeeded" };
        // if (state.isListView && state.isListView.data) {
        //   state.isListView.data = [action.payload, ...state.isListView.data];
        // } else {
        //   state.isListView = { data: [action.payload] };
        // }
      })
      .addCase(appointmentStoreApi.rejected, (state) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentStore: "failed" };
      })
      .addCase(appointmentDepositStoreApi.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentDepositStore: "loading" };
      })
      .addCase(appointmentDepositStoreApi.fulfilled, (state) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentDepositStore: "succeeded" };
      })
      .addCase(appointmentDepositStoreApi.rejected, (state) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentDepositStore: "failed" };
      })
      .addCase(appointmentUpdateApi.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentUpdate: "loading" };
      })
      .addCase(appointmentUpdateApi.fulfilled, (state) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentUpdate: "succeeded" };
      })
      .addCase(appointmentUpdateApi.rejected, (state) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentUpdate: "failed" };
      })
      .addCase(appointmentRescheduleApi.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentReschedule: "loading" };
      })
      .addCase(appointmentRescheduleApi.fulfilled, (state) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentReschedule: "succeeded" };
      })
      .addCase(appointmentRescheduleApi.rejected, (state) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentReschedule: "failed" };
      })
      .addCase(appointmentResizeApi.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentResize: "loading" };
      })
      .addCase(appointmentResizeApi.fulfilled, (state) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentResize: "succeeded" };
      })
      .addCase(appointmentResizeApi.rejected, (state) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentResize: "failed" };
      })
      .addCase(refundPaymentStoreApi.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, refundPaymentStoreApi: "loading" };
      })
      .addCase(refundPaymentStoreApi.fulfilled, (state) => {
        state.isApiStatus = { ...state.isApiStatus, refundPaymentStoreApi: "succeeded" };
      })
      .addCase(refundPaymentStoreApi.rejected, (state) => {
        state.isApiStatus = { ...state.isApiStatus, refundPaymentStoreApi: "failed" };
      })
      .addCase(appointmentFrameRefreshApi.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentFrameRefreshApi: "loading" };
      })
      .addCase(appointmentFrameRefreshApi.fulfilled, (state, action) => {
        const payload = action.payload;
        const window = getFrameWindow(action.meta && action.meta.arg ? action.meta.arg.appointments : null);
        if (Array.isArray(payload)) {
          state.isListView = mergeCalendarFrame(state.isListView, payload, window, action.meta.arg);
        } else if (payload && Array.isArray(payload.data)) {
          const current = Array.isArray(state.isListView) ? state.isListView : state.isListView?.data;
          state.isListView = { ...payload, data: mergeCalendarFrame(current, payload.data, window, action.meta.arg) };
        } else {
          state.isListView = payload;
        }
        state.isApiStatus = { ...state.isApiStatus, appointmentFrameRefreshApi: "succeeded" };
      })
      .addCase(appointmentFrameRefreshApi.rejected, (state) => {
        // The grid already has data; a failed frame refresh must not blank it.
        state.isApiStatus = { ...state.isApiStatus, appointmentFrameRefreshApi: "failed" };
      })
      .addCase(appointmentListViewApi.pending, (state, action) => {
        // Only the most recent request may write the grid (WEEK-03).
        state.latestListViewRequestId = action.meta.requestId;
        if (!action.meta.arg?.silent) {
          state.isListViewError = "";
          state.isApiStatus = { ...state.isApiStatus, appointmentListViewApi: "loading" };
        }
      })
      .addCase(AppointmentAutofillserviceOptionsApi.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, AppointmentAutofillserviceOptionsApi: "loading" };
      })
      .addCase(AppointmentAutofillserviceOptionsApi.fulfilled, (state, action) => {
        state.isApiStatus = { ...state.isApiStatus, AppointmentAutofillserviceOptionsApi: "succeeded" };
        state.isAppointmentAutofillserviceOptions = action.payload;
      })
      .addCase(AppointmentAutofillserviceOptionsApi.rejected, (state) => {
        state.isApiStatus = { ...state.isApiStatus, AppointmentAutofillserviceOptionsApi: "failed" };
        state.isAppointmentAutofillserviceOptions = [];
      })
      .addCase(appointmentListViewApi.fulfilled, (state, action) => {
        if (state.latestListViewRequestId && state.latestListViewRequestId !== action.meta.requestId) return;
        let old_current_page = state.isListView.current_page ? state.isListView.current_page : "";
        let new_current_page = action.payload.current_page ? action.payload.current_page : "";
        let viewdata = state.isListView && state.isListView.data;
        let newviewdata = action.payload && action.payload.data;
        state.isListView = action.payload;
        if (old_current_page && new_current_page && old_current_page < new_current_page && old_current_page != new_current_page) {
          viewdata && newviewdata ? (state.isListView.data = [...viewdata, ...newviewdata]) : action.payload;
        }
        state.isApiStatus = { ...state.isApiStatus, appointmentListViewApi: "succeeded" };
        state.isListView = action.payload;
        state.isListViewError = "";
      })
      .addCase(appointmentListViewApi.rejected, (state, action) => {
        if (state.latestListViewRequestId && state.latestListViewRequestId !== action.meta.requestId) return;
        if (!action.meta.arg?.silent) {
          state.isApiStatus = { ...state.isApiStatus, appointmentListViewApi: "failed" };
          state.isListViewError = listErrorMessage(action, "Unable to load calendar appointments.");
        }
      })
      .addCase(ClientAppointmentListViewApi.pending, (state, action) => {
        const request = clientAppointmentRequest(action.meta.arg);
        const query = JSON.stringify([String(request.client_id), request.filter || ""]);
        if (query !== state.clientAppointmentQuery) state.isClientDetailAppointmentListView = [];
        state.clientAppointmentQuery = query;
        state.clientAppointmentClientId = request.client_id;
        state.clientAppointmentRequestId = action.meta.requestId;
        state.isClientDetailAppointmentListViewError = "";
        state.isApiStatus.ClientAppointmentListViewApi = "loading";
      })
      .addCase(ClientAppointmentListViewApi.fulfilled, (state, action) => {
        if (state.clientAppointmentRequestId && state.clientAppointmentRequestId !== action.meta.requestId) return;
        const previous = state.isClientDetailAppointmentListView;
        const incoming = action.payload || {};
        const append = action.meta.arg?.next_page_url && Number(incoming.current_page) > Number(previous.current_page);
        state.isClientDetailAppointmentListView = append ? {
          ...incoming, data: [...(previous.data || []), ...(incoming.data || [])],
        } : incoming;
        state.isApiStatus.ClientAppointmentListViewApi = "succeeded";
        state.isClientDetailAppointmentListViewError = "";
      })
      .addCase(ClientAppointmentListViewApi.rejected, (state, action) => {
        if (state.clientAppointmentRequestId && state.clientAppointmentRequestId !== action.meta.requestId) return;
        state.isApiStatus.ClientAppointmentListViewApi = "failed";
        state.isClientDetailAppointmentListViewError = listErrorMessage(action, "Unable to load client appointments.");
      })
      .addCase(appointmentDetailApi.pending, (state, action) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentDetailApi: "loading" };
        state.isDetailData = null;
        state.appointmentDetailRequestId = action.meta.requestId;
      })
      .addCase(appointmentDetailApi.fulfilled, (state, action) => {
        if (state.appointmentDetailRequestId && state.appointmentDetailRequestId !== action.meta.requestId) return;
        state.isApiStatus = { ...state.isApiStatus, appointmentDetailApi: "succeeded" };
        state.isDetailData = normalizeAppointmentDetail(action.payload);
      })
      .addCase(appointmentDetailApi.rejected, (state, action) => {
        if (state.appointmentDetailRequestId && state.appointmentDetailRequestId !== action.meta.requestId) return;
        state.isApiStatus = { ...state.isApiStatus, appointmentDetailApi: "failed" };
        state.isDetailData = null;
      })
      .addCase(appointmentDeleteApi.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentDelete: "loading" };
      })
      .addCase(appointmentDeleteApi.fulfilled, (state) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentDelete: "succeeded" };
      })
      .addCase(appointmentDeleteApi.rejected, (state) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentDelete: "failed" };
      })
      .addCase(appointmentPaymentConfirmApi.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentPaymentConfirmApi: "loading" };
      })
      .addCase(appointmentPaymentConfirmApi.fulfilled, (state, action) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentPaymentConfirmApi: "succeeded" };
        state.isAppointmentPaymentConfirmApi = action.payload;
      })
      .addCase(appointmentPaymentConfirmApi.rejected, (state) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentPaymentConfirmApi: "failed" };
        state.isAppointmentPaymentConfirmApi = [];
      })
      .addCase(appointmentPaymentFailedApi.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentPaymentFailedApi: "loading" };
      })
      .addCase(appointmentPaymentFailedApi.fulfilled, (state, action) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentPaymentFailedApi: "succeeded" };
        state.isAppointmentPaymentFailedApi = action.payload;
      })
      .addCase(appointmentPaymentFailedApi.rejected, (state) => {
        state.isApiStatus = { ...state.isApiStatus, appointmentPaymentFailedApi: "failed" };
        state.isAppointmentPaymentFailedApi = [];
      })
      .addCase(AppointmentResourceItemsApi.pending, (state, action) => {
        state.isApiStatus = { ...state.isApiStatus, AppointmentResourceItemsApi: "loading" };
        state.latestAppointmentResourceRequestId = action.meta.requestId;
      })
      .addCase(AppointmentResourceItemsApi.fulfilled, (state, action) => {
        if (action.meta?.requestId && state.latestAppointmentResourceRequestId && state.latestAppointmentResourceRequestId !== action.meta.requestId) return;
        state.isApiStatus = { ...state.isApiStatus, AppointmentResourceItemsApi: "succeeded" };
        state.isAppointmentResourceItems = normalizeResourceOptionLabels(action.payload);
        state.appointmentResourceAvailabilityContext = action.meta?.arg?.availebleTimeObj || null;
      })
      .addCase(AppointmentResourceItemsApi.rejected, (state, action) => {
        if (action.meta?.requestId && state.latestAppointmentResourceRequestId && state.latestAppointmentResourceRequestId !== action.meta.requestId) return;
        if (action.meta?.aborted || action.payload?.cancelled) {
          state.isApiStatus = { ...state.isApiStatus, AppointmentResourceItemsApi: "idle" };
          return;
        }
        state.isApiStatus = { ...state.isApiStatus, AppointmentResourceItemsApi: "failed" };
        state.isAppointmentResourceItems = [];
      })
      .addCase(ClientInvoiceSaleCartApi.pending, (state) => {
        state.isApiStatus = { ...state.isApiStatus, ClientInvoiceSaleCartApi: "loading" };
      })
      .addCase(ClientInvoiceSaleCartApi.fulfilled, (state, action) => {
        state.isApiStatus = { ...state.isApiStatus, ClientInvoiceSaleCartApi: "succeeded" };
        state.isClientInvoiceSaleCart = action.payload;
      })
      .addCase(ClientInvoiceSaleCartApi.rejected, (state) => {
        state.isApiStatus = { ...state.isApiStatus, ClientInvoiceSaleCartApi: "failed" };
        state.isClientInvoiceSaleCart = [];
      })
      .addCase(BookNextAvailableResourceApi.pending, (state, action) => {
        state.nextResourceRequestId = action.meta.requestId;
        state.isApiStatus = { ...state.isApiStatus, BookNextAvailableResourceApi: "loading" };
      })
      .addCase(BookNextAvailableResourceApi.fulfilled, (state, action) => {
        if (action.meta?.requestId && state.nextResourceRequestId !== action.meta.requestId) return;
        state.isApiStatus = { ...state.isApiStatus, BookNextAvailableResourceApi: "succeeded" };
        state.isBookNextAvailableResource = action.payload;
      })
      .addCase(BookNextAvailableResourceApi.rejected, (state, action) => {
        if (action.meta?.requestId && state.nextResourceRequestId !== action.meta.requestId) return;
        state.isApiStatus = { ...state.isApiStatus, BookNextAvailableResourceApi: "failed" };
        state.isBookNextAvailableResource = [];
      });
  },
});
// Action creators are generated for each case reducer function
export const {
  reset,
  AddAppointmentForm,
  EditAppointmentForm,
  AppointmentDetailModal,
  openAppointmentFilter,
  closeAppointmentFilter,
  RescheduleAppointmentForm,
  AddOnServiceTab,
  ShowLoadingSpinner,
  OpenAppointmentAutoFillModal,
  AppointmentAutofillData,
  AppointmentAutoFillFinalData,
  AppointmentAvailableTimeLoader,
  AppointmentAvailableTime,
  OpenInvoiceStripePayModal,
  OpenDepositPayTab,
  CalendarAddFormSetActivebtnState,
  serviceDepositData,
  GetStripeTokenAppointment,
  OpenAppointmentRefundModal,
  GetCancelledAppointmentData,
  OpenAppointmentcancelRefundStripe,
  GetAppointmentcancelRefundStripe,
  EditAppointmentFormData,
  OpenEditAppointmentRefunds,
  CancellationReason,
  CalendarEditFormSetActivebtnState,
  CancelDepositBalance,
  AddOnServiceButtonRender,
  ClearAppointmentDetailData,
  StaffBusinessHourArrays,
  AppointmentResourceItemsClears,
  BookNextModalOpen,
  clientAppointmentFilterStatus,
  clientAppointmentFilterStatusClear
} = appointmentSlice.actions;
export default appointmentSlice.reducer;

// Guard the first render after the client changes, before its request effect runs.
export const selectClientAppointmentHistory = state => {
  const owner = state.appointment.clientAppointmentClientId;
  const clientId = state.client.isDetailData?.id;
  return owner != null && clientId != null && String(owner) === String(clientId)
    ? state.appointment.isClientDetailAppointmentListView : null;
};
